import { useEffect, useMemo, useState } from "react";
import { CheckboxProps } from "@mui/material";
import { useSnackbar } from "notistack";
import { Space } from "~/lib/types";
import {
  ComplianceControl,
  ComplianceFramework,
  DocumentMetadata,
  DocumentMetadataConnection,
  DocumentStatus,
  ListDocumentsDocument,
  useDeleteDocumentMutation,
  useListDocumentsQuery,
  useRequestDocumentDownloadUrlMutation,
} from "~/operations";
import { pluralize } from "~/lib/pluralize";
import { getError } from "~/lib/handle-error";
import { downloadFromUrl } from "~/lib/download-from-url";

export interface UseReportExports {
  selectedReports: ComplianceFramework["mrn"][] | ComplianceControl["mrn"][];
  handle: {
    selectionChange: (checked: CheckboxProps["checked"], mrn: string) => void;
    selectAll: (checked: boolean, from: number, to: number) => void;
    downloadSingle: (report: DocumentMetadata) => void;
    delete: () => void;
    deleteSingle: (documentMrn: DocumentMetadata["mrn"]) => void;
    cancel: () => void;
    fetchMore: ReturnType<typeof useListDocumentsQuery>["fetchMore"];
  };
  documentList: {
    data: DocumentMetadataConnection | undefined;
    reports: DocumentMetadata[];
    error: ReturnType<typeof useListDocumentsQuery>["error"];
    loading: ReturnType<typeof useListDocumentsQuery>["loading"];
  };
  deleteDialog: {
    isOpen: boolean;
    close: () => void;
    confirmDelete: () => Promise<void>;
    isDeleting: boolean;
  };
}

interface UseReportProps {
  space: Space;
  onDeleteSucceed: () => void;
}

export const useReports = ({
  space,
  onDeleteSucceed,
}: UseReportProps): UseReportExports => {
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [singleReportMrn, setSingleReportMrn] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { data, error, loading, fetchMore, startPolling, stopPolling } =
    useListDocumentsQuery({
      variables: {
        scopeMrn: space.mrn,
        first: 10,
      },
      fetchPolicy: "cache-and-network",
    });

  // every time we get new data, lets check if we have any new reports
  // that are currently in a state of processing so we can determine when to
  // start and stop polling
  const hasProcessingReports = useMemo(() => {
    const processingReports = data?.listDocuments?.edges
      .map((edge) => edge.node)
      .filter(
        (report) =>
          report.status === DocumentStatus.Pending ||
          report.status === DocumentStatus.Running ||
          report.status === DocumentStatus.Queued,
      );

    return processingReports && processingReports.length > 0;
  }, [data]);

  // start polling
  useEffect(() => {
    if (hasProcessingReports) {
      startPolling(5000);
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [hasProcessingReports]);

  const [deleteDocumentMutation] = useDeleteDocumentMutation({
    refetchQueries: [ListDocumentsDocument],
  });

  const [requestDocumentDownloadUrlMutation] =
    useRequestDocumentDownloadUrlMutation();

  const handleSelectionChange = (
    checked: CheckboxProps["checked"],
    mrn: string,
  ) => {
    let nextSelection: typeof selectedReports = [];
    if (checked) {
      nextSelection = [...selectedReports, mrn];
    } else {
      nextSelection = selectedReports.filter((x) => x !== mrn);
    }

    setSelectedReports(nextSelection);
  };

  const handleSelectAll = (checked: boolean, from: number, to: number) => {
    const currentPageReports = (data?.listDocuments?.edges || [])
      .map((edge) => edge.node)
      .slice(from, to);

    const completedReportsMrns = currentPageReports
      .filter(
        (node) =>
          node.status === DocumentStatus.Completed ||
          node.status === DocumentStatus.Failed,
      ) // Only finished reports
      .map((node) => node.mrn);

    if (!checked) {
      setSelectedReports((alreadySelectedReports) =>
        alreadySelectedReports.filter(
          (mrn) => !completedReportsMrns.includes(mrn),
        ),
      );
      return;
    }

    setSelectedReports((alreadySelectedReports) => {
      const combinedSet = new Set([
        ...alreadySelectedReports,
        ...completedReportsMrns,
      ]);
      return [...combinedSet];
    });
  };

  const handleDownloadSingle = async (report: DocumentMetadata) => {
    try {
      const request = await requestDocumentDownloadUrlMutation({
        variables: { documentMrn: report.mrn },
      });

      let url = request.data?.requestDocumentDownloadUrl?.downloadUrl;
      if (url) {
        downloadFromUrl(url, report.name, { openInNew: true });
        return;
      }

      throw new Error("No download URL was returned");
    } catch (e) {
      const errorMessage = getError(e);
      enqueueSnackbar(
        `Something went wrong while attempting to download: ${errorMessage}`,
        { variant: "error" },
      );
    }
  };

  const openDeleteDialogForBulk = () => {
    setDeleteDialogOpen(true);
  };

  const openDeleteDialogForSingle = (documentMrn: string) => {
    setSingleReportMrn(documentMrn);
    setSelectedReports([documentMrn]);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setSingleReportMrn(null);
    setDeleteDialogOpen(false);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);

    try {
      // Single delete
      if (singleReportMrn) {
        await deleteDocumentMutation({
          variables: {
            documentMrn: singleReportMrn,
          },
        });

        enqueueSnackbar(`Successfully deleted report`, { variant: "success" });

        // Remove the deleted report from the selected reports in case it was selected previously
        const nextSelection = selectedReports.filter(
          (x) => x !== singleReportMrn,
        );

        setSelectedReports(nextSelection);
      } else {
        // Bulk delete
        await Promise.all(
          selectedReports.map((documentMrn) =>
            deleteDocumentMutation({
              variables: {
                documentMrn,
              },
            }),
          ),
        );

        enqueueSnackbar(
          `Successfully deleted ${selectedReports.length} ${pluralize(
            "report",
            selectedReports.length,
          )}`,
          { variant: "success" },
        );

        setSelectedReports([]);
        onDeleteSucceed();
      }
    } catch (e) {
      const errorMessage = getError(e);
      const reportsCount = singleReportMrn ? 1 : selectedReports.length;
      enqueueSnackbar(
        `Something went wrong while attempting to delete ${pluralize(
          "report",
          reportsCount,
        )}: ${errorMessage}`,
        { variant: "error" },
      );
    } finally {
      setIsDeleting(false);
      closeDeleteDialog();
    }
  };

  const handleCancel = () => {
    setSelectedReports([]);
  };

  return {
    selectedReports,
    handle: {
      selectionChange: handleSelectionChange,
      selectAll: handleSelectAll,
      downloadSingle: handleDownloadSingle,
      delete: openDeleteDialogForBulk,
      deleteSingle: openDeleteDialogForSingle,
      cancel: handleCancel,
      fetchMore,
    },
    documentList: {
      data: data?.listDocuments,
      reports: data?.listDocuments?.edges?.map((edge) => edge.node) || [],
      error,
      loading,
    },
    deleteDialog: {
      isOpen: deleteDialogOpen,
      close: closeDeleteDialog,
      confirmDelete,
      isDeleting,
    },
  };
};
